import { TableCell, TableRow, Tooltip } from '@mui/material';
import { FC, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MarketSupplier } from '../../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../../components/shared/useUser';
import { isDemoModeEnabled } from '../../../../utils/featureAccess';

type Props = {
  supplier: MarketSupplier;
};

const SupplierItemRow: FC<Props> = ({ supplier }) => {
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const [searchParams] = useSearchParams();

  const canEdit = hasPermissionTo(['ConfigurationAdmin']) || hasPermissionTo(['SupplierUpdate']);

  const handleRowClick = () => {
    if (!canEdit) return;

    // Demo Mode routes to the new Supplier Profile detail screen (mocked data, additive -
    // see `SupplierProfile/index.tsx`) instead of the production edit form.
    if (isDemoModeEnabled()) {
      navigate(`/procurement/suppliers/profile/${supplier.supplierId}`);
      return;
    }

    const params = new URLSearchParams();
    const companyId = searchParams.get('companyId');
    const customerTypeId = searchParams.get('customerTypeId');
    const searchTerm = searchParams.get('searchTerm');

    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    navigate(`/procurement/suppliers/edit/${supplier.supplierId}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatCutOffTime = () => {
    if (!supplier.cutOffTime) return '-';

    if (supplier.timezoneOffset !== undefined && supplier.timezoneOffset !== null) {
      const [hours, minutes] = supplier.cutOffTime.split(':').map(Number);

      const totalMinutes = hours * 60 + minutes + Number(supplier.timezoneOffset) * 60;
      const adjustedTotalMinutes = ((totalMinutes % 1440) + 1440) % 1440;
      const adjustedHours = Math.floor(adjustedTotalMinutes / 60);
      const adjustedMinutes = adjustedTotalMinutes % 60;

      const formatted = `${String(adjustedHours).padStart(2, '0')}:${String(adjustedMinutes).padStart(2, '0')}`;
      return supplier.timezone ? `${formatted} (${supplier.timezone})` : formatted;
    }

    return supplier.timezone ? `${supplier.cutOffTime} (${supplier.timezone})` : supplier.cutOffTime;
  };

  return (
    <TableRow
      onClick={canEdit ? handleRowClick : undefined}
      sx={{
        cursor: canEdit ? 'pointer' : 'default',
        '&:hover': canEdit ? {
          backgroundColor: 'action.hover',
        } : undefined,
      }}
    >
      <TableCell>
        {supplier.supplierNumber}
      </TableCell>
      <TableCell>
        <Tooltip 
          title={supplier.supplierName} 
          placement="top"
        >
          <span>{truncateText(supplier.supplierName)}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip 
          title={supplier.emailAddress} 
          placement="top"
        >
          <span>{truncateText(supplier.emailAddress)}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        {supplier.fileFormat}
      </TableCell>
      <TableCell>
        {supplier.publishMethod}
      </TableCell>
      <TableCell align="center">
        {formatCutOffTime()}
      </TableCell>
      <TableCell>
        <Tooltip 
          title={supplier.instanceName} 
          placement="top"
        >
          <span>{truncateText(supplier.instanceName)}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip 
          title={supplier.marketName || 'All Markets'} 
          placement="top"
        >
          <span>{truncateText(supplier.marketName || 'All Markets')}</span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default SupplierItemRow;
